import { c as _c } from "react/compiler-runtime";
import { DropdownMenuItem, DropdownMenuSubMenu, DropdownMenuSubMenuContent, DropdownMenuSubMenuTrigger } from "../../dropdown";
import Tooltip from "../../tooltip";
import { useActionsContext } from "../table-filters/context";
import React, { useCallback, useMemo, useState } from "react";
import { IoIosClose } from "react-icons/io";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { cx } from "@/lib/tailwind-utils";
import { FilterEditContext, type FilterEditContextValue } from "./edit-context";
import { singleBooleanValue } from "./filter-types/boolean";
import { BOOLEAN_FILTER_OPTIONS } from "./filter-types/boolean-options";
import { formatFilterDisplayValue } from "./helpers";
import { useFilterFocus } from "./hooks";
import { FilterInput, filterContentWidthClass } from "./render-input";
import type { FilterTypeProps } from "./types";
const isEmptyValue = (value: any) => value === undefined || value === null || value === "" || Array.isArray(value) && value.length === 0;

/**
 * A boolean filter offering exactly Yes and No (no relabelled or "Not set"
 * choices), which the list can show as a switch.
 */
const isPlainYesNo = (type: string, options: FilterTypeProps["options"]) => {
  if (type !== "boolean") return false;
  const choices = options?.length ? options : BOOLEAN_FILTER_OPTIONS;
  return choices.length === BOOLEAN_FILTER_OPTIONS.length && BOOLEAN_FILTER_OPTIONS.every(plain => choices.some(choice => String(choice.value) === plain.value && choice.label === plain.label));
};

/**
 * The switch drawn inside a filter row. The row itself is the control, so
 * this is presentational. "unset" is a greyed track with the thumb centred:
 * neither on nor off, the filter isn't applied.
 */
const FilterSwitch = t0 => {
  const $ = _c(12);
  const {
    state
  } = t0;
  const t1 = state === "on" ? "bg-primary" : "bg-muted";
  const t2 = state === "unset" && "opacity-40";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = cx("pointer-events-none relative ml-3 inline-flex h-4 w-7 shrink-0 items-center rounded-full p-0.5 shadow-inner ring-1 ring-inset ring-border/5 transition-all", t1, t2);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = state === "on" && "translate-x-2";
  const t5 = state === "off" && "translate-x-0";
  const t6 = state === "unset" && "translate-x-1";
  let t7;
  if ($[3] !== t4 || $[4] !== t5 || $[5] !== t6) {
    t7 = cx("h-3 w-4 rounded-full bg-background/80 shadow-lg backdrop-blur-sm transition-transform duration-150 ease-in-out", t4, t5, t6);
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== t7) {
    t8 = <span className={t7} />;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t3 || $[10] !== t8) {
    t9 = <span aria-hidden="true" className={t3}>{t8}</span>;
    $[9] = t3;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
};

/**
 * DropdownTableFilter — renders a filter as a sub-menu item inside the main
 * Filters dropdown panel.
 *
 * Write semantics: every edit writes through to `filterSetter` immediately.
 * The parent (TableFiltersProvider) buffers those writes and flushes on
 * main-panel close. We can't buffer locally here because Radix unmounts
 * sub-menus on outside-click without firing onOpenChange(false), which
 * would lose buffered edits.
 */
const DropdownTableFilter: React.FC<FilterTypeProps> = t0 => {
  const $ = _c(110);
  const {
    label,
    type,
    filterValue,
    filterSetter,
    options,
    disableClear: t1,
    icon: Icon,
    onBack,
    direction
  } = t0;
  const disableClear = t1 === undefined ? false : t1;
  const {
    isMobile
  } = useIsMobile();
  const clock = useClinicClock();
  const {
    focusRef,
    inputRef
  } = useFilterFocus();
  const [isOpen, setIsOpen] = useState(false);
  const {
    setOpen: setPanelOpen
  } = useActionsContext();
  let t2;
  if ($[0] !== filterSetter) {
    t2 = e => {
      e.stopPropagation();
      filterSetter(undefined);
    };
    $[0] = filterSetter;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleClearFilter = t2;
  let t3;
  if ($[2] !== filterValue) {
    t3 = {
      value: filterValue
    };
    $[2] = filterValue;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== setPanelOpen) {
    t4 = () => setPanelOpen(false);
    $[4] = setPanelOpen;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== filterSetter || $[7] !== t4) {
    t5 = {
      onChange: filterSetter,
      submit: t4
    };
    $[6] = filterSetter;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== direction || $[10] !== focusRef || $[11] !== label || $[12] !== onBack || $[13] !== options || $[14] !== type) {
    t6 = {
      type,
      label,
      options,
      onBack,
      focusRef,
      direction
    };
    $[9] = direction;
    $[10] = focusRef;
    $[11] = label;
    $[12] = onBack;
    $[13] = options;
    $[14] = type;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t3 || $[17] !== t5 || $[18] !== t6) {
    t7 = {
      state: t3,
      actions: t5,
      meta: t6
    };
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const editContextValue = t7;
  let t8;
  if ($[20] !== inputRef) {
    t8 = e_0 => {
      if (e_0.target !== e_0.currentTarget) {
        return;
      }
      const el = e_0.currentTarget;
      if (el instanceof HTMLElement && el.dataset.state === "open" && inputRef.current) {
        inputRef.current.focus();
      }
    };
    $[20] = inputRef;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleContentAnimationEnd = t8;
  const [openSide, setOpenSide] = useState("right");
  let t9;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = node => {
      if (!node) {
        return;
      }
      const sync = () => {
        const side = node.dataset.side;
        if (side === "left" || side === "right") {
          setOpenSide(side);
        }
      };
      sync();
      const observer = new MutationObserver(sync);
      observer.observe(node, {
        attributes: true,
        attributeFilter: ["data-side"]
      });
      return () => observer.disconnect();
    };
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const trackOpenSide = t9;
  if (type === "toggle") {
    const checked = filterValue === true;
    let t10;
    if ($[23] !== checked || $[24] !== filterSetter) {
      t10 = event => {
        event.preventDefault();
        filterSetter(checked ? undefined : true);
      };
      $[23] = checked;
      $[24] = filterSetter;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    const t11 = checked && "bg-accent";
    let t12;
    if ($[26] !== t11) {
      t12 = cx("justify-between rounded-lg", t11);
      $[26] = t11;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    let t13;
    if ($[28] !== Icon) {
      t13 = Icon && <Icon className="mr-1.5 h-4 w-4 text-muted-foreground" />;
      $[28] = Icon;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== label) {
      t14 = <span>{label}</span>;
      $[30] = label;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    let t15;
    if ($[32] !== t13 || $[33] !== t14) {
      t15 = <span className="flex items-center">{t13}{t14}</span>;
      $[32] = t13;
      $[33] = t14;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    const t16 = checked ? "on" : "off";
    let t17;
    if ($[35] !== t16) {
      t17 = <FilterSwitch state={t16} />;
      $[35] = t16;
      $[36] = t17;
    } else {
      t17 = $[36];
    }
    let t18;
    if ($[37] !== checked || $[38] !== t10 || $[39] !== t12 || $[40] !== t15 || $[41] !== t17) {
      t18 = <DropdownMenuItem role="menuitemcheckbox" aria-checked={checked} onSelect={t10} className={t12}>{t15}{t17}</DropdownMenuItem>;
      $[37] = checked;
      $[38] = t10;
      $[39] = t12;
      $[40] = t15;
      $[41] = t17;
      $[42] = t18;
    } else {
      t18 = $[42];
    }
    return t18;
  }
  if (isPlainYesNo(type, options)) {
    const current = singleBooleanValue(filterValue);
    const state = String(current) === "true" ? "on" : String(current) === "false" ? "off" : "unset";
    const isSet = state !== "unset";
    const canClear = isSet && !disableClear;
    const t10 = state === "on" ? true : state === "off" ? false : "mixed";
    const t11 = canClear ? "Backspace Delete" : undefined;
    let t12;
    if ($[43] !== filterSetter || $[44] !== state) {
      t12 = event_0 => {
        event_0.preventDefault();
        filterSetter(state === "on" ? "false" : "true");
      };
      $[43] = filterSetter;
      $[44] = state;
      $[45] = t12;
    } else {
      t12 = $[45];
    }
    let t13;
    if ($[46] !== canClear || $[47] !== filterSetter) {
      t13 = event_1 => {
        if (canClear && (event_1.key === "Backspace" || event_1.key === "Delete")) {
          event_1.preventDefault();
          filterSetter(undefined);
        }
      };
      $[46] = canClear;
      $[47] = filterSetter;
      $[48] = t13;
    } else {
      t13 = $[48];
    }
    const t14 = isSet && "bg-accent";
    let t15;
    if ($[49] !== t14) {
      t15 = cx("justify-between rounded-lg", t14);
      $[49] = t14;
      $[50] = t15;
    } else {
      t15 = $[50];
    }
    let t16;
    if ($[51] !== Icon || $[52] !== canClear || $[53] !== filterSetter) {
      t16 = canClear ? <span aria-hidden="true" onClick={event_2 => {
        event_2.preventDefault();
        event_2.stopPropagation();
        filterSetter(undefined);
      }} className="relative z-20 mr-1 inline-flex cursor-pointer items-center justify-center rounded hover:bg-hover"><IoIosClose className="h-4 w-4" /></span> : Icon ? <Icon className="mr-1.5 h-4 w-4 text-muted-foreground" /> : null;
      $[51] = Icon;
      $[52] = canClear;
      $[53] = filterSetter;
      $[54] = t16;
    } else {
      t16 = $[54];
    }
    let t17;
    if ($[55] !== label) {
      t17 = <span>{label}</span>;
      $[55] = label;
      $[56] = t17;
    } else {
      t17 = $[56];
    }
    let t18;
    if ($[57] !== t16 || $[58] !== t17) {
      t18 = <span className="flex items-center">{t16}{t17}</span>;
      $[57] = t16;
      $[58] = t17;
      $[59] = t18;
    } else {
      t18 = $[59];
    }
    let t19;
    if ($[60] !== state) {
      t19 = <FilterSwitch state={state} />;
      $[60] = state;
      $[61] = t19;
    } else {
      t19 = $[61];
    }
    let t20;
    if ($[62] !== t10 || $[63] !== t11 || $[64] !== t12 || $[65] !== t13 || $[66] !== t15 || $[67] !== t18 || $[68] !== t19) {
      t20 = <DropdownMenuItem role="menuitemcheckbox" aria-checked={t10} aria-keyshortcuts={t11} onSelect={t12} onKeyDown={t13} className={t15}>{t18}{t19}</DropdownMenuItem>;
      $[62] = t10;
      $[63] = t11;
      $[64] = t12;
      $[65] = t13;
      $[66] = t15;
      $[67] = t18;
      $[68] = t19;
      $[69] = t20;
    } else {
      t20 = $[69];
    }
    return t20;
  }
  if (options?.length === 0) {
    return null;
  }
  let t10;
  if ($[70] !== clock || $[71] !== filterValue || $[72] !== options || $[73] !== type) {
    t10 = formatFilterDisplayValue(filterValue, type, options, clock);
    $[70] = clock;
    $[71] = filterValue;
    $[72] = options;
    $[73] = type;
    $[74] = t10;
  } else {
    t10 = $[74];
  }
  const {
    fullDisplayValue,
    truncatedDisplayValue
  } = t10;
  let t11;
  if ($[75] !== Icon || $[76] !== disableClear || $[77] !== filterValue || $[78] !== handleClearFilter) {
    t11 = !isEmptyValue(filterValue) && !disableClear ? <span role="button" aria-label="Clear filter" tabIndex={-1} onMouseDown={handleClearFilter} className="relative z-20 mr-1 inline-flex cursor-pointer items-center justify-center rounded hover:bg-hover"><IoIosClose className="h-4 w-4" /></span> : Icon ? <Icon className="mr-1.5 h-4 w-4 text-muted-foreground" /> : null;
    $[75] = Icon;
    $[76] = disableClear;
    $[77] = filterValue;
    $[78] = handleClearFilter;
    $[79] = t11;
  } else {
    t11 = $[79];
  }
  let t12;
  if ($[80] !== filterValue || $[81] !== fullDisplayValue || $[82] !== truncatedDisplayValue) {
    t12 = !isEmptyValue(filterValue) && truncatedDisplayValue && <><div className="mx-2 h-3 self-center border-l" /><span className="text-caption-emphasis text-primary"><Tooltip content={fullDisplayValue}>{truncatedDisplayValue}</Tooltip></span></>;
    $[80] = filterValue;
    $[81] = fullDisplayValue;
    $[82] = truncatedDisplayValue;
    $[83] = t12;
  } else {
    t12 = $[83];
  }
  let t13;
  if ($[84] !== label || $[85] !== t11 || $[86] !== t12) {
    t13 = <>{t11}{label}{t12}</>;
    $[84] = label;
    $[85] = t11;
    $[86] = t12;
    $[87] = t13;
  } else {
    t13 = $[87];
  }
  const triggerContent = t13;
  let t14;
  if ($[88] !== filterValue) {
    t14 = !isEmptyValue(filterValue) && "bg-accent";
    $[88] = filterValue;
    $[89] = t14;
  } else {
    t14 = $[89];
  }
  let t15;
  if ($[90] !== t14) {
    t15 = cx("rounded-lg", t14);
    $[90] = t14;
    $[91] = t15;
  } else {
    t15 = $[91];
  }
  let t16;
  if ($[92] !== openSide || $[93] !== t15 || $[94] !== triggerContent) {
    t16 = <DropdownMenuSubMenuTrigger className={t15} arrowDirection={openSide}>{triggerContent}</DropdownMenuSubMenuTrigger>;
    $[92] = openSide;
    $[93] = t15;
    $[94] = triggerContent;
    $[95] = t16;
  } else {
    t16 = $[95];
  }
  let t17;
  if ($[96] !== type) {
    t17 = cx("min-w-0 overflow-visible", filterContentWidthClass(type));
    $[96] = type;
    $[97] = t17;
  } else {
    t17 = $[97];
  }
  const t18 = isMobile ? -40 : -1;
  let t19;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="min-w-[min(200px,calc(var(--radix-popper-available-width)-12px))] px-1.5 py-1"><FilterInput /></div>;
    $[98] = t19;
  } else {
    t19 = $[98];
  }
  let t20;
  if ($[99] !== editContextValue) {
    t20 = <FilterEditContext value={editContextValue}>{t19}</FilterEditContext>;
    $[99] = editContextValue;
    $[100] = t20;
  } else {
    t20 = $[100];
  }
  let t21;
  if ($[101] !== handleContentAnimationEnd || $[102] !== t17 || $[103] !== t18 || $[104] !== t20) {
    t21 = <DropdownMenuSubMenuContent ref={trackOpenSide} className={t17} sideOffset={t18} onAnimationEnd={handleContentAnimationEnd}>{t20}</DropdownMenuSubMenuContent>;
    $[101] = handleContentAnimationEnd;
    $[102] = t17;
    $[103] = t18;
    $[104] = t20;
    $[105] = t21;
  } else {
    t21 = $[105];
  }
  let t22;
  if ($[106] !== isOpen || $[107] !== t16 || $[108] !== t21) {
    t22 = <DropdownMenuSubMenu open={isOpen} onOpenChange={setIsOpen}>{t16}{t21}</DropdownMenuSubMenu>;
    $[106] = isOpen;
    $[107] = t16;
    $[108] = t21;
    $[109] = t22;
  } else {
    t22 = $[109];
  }
  return t22;
};
export default DropdownTableFilter;